'use client';

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, Grid, TextField, Slider, Button, Stack, Paper, CircularProgress, Select, MenuItem, InputLabel, FormControl, IconButton, Alert } from '@mui/material';
import { calculateMetrics, CalculatorInputs, projectRevenueTimeline } from '../../../lib/socialCalculator';
import { useSession } from 'next-auth/react';
import { Icon } from '@iconify/react';
import Link from 'next/link';
import ArrowBackIcon from '@/letsgo/icons/ArrowBack';
import { getToolIconForPath } from '@/lib/toolsData';
import { useAiActionCooldown } from '@/hooks/useAiActionCooldown';

/** Parse a single row of CSV into CalculatorInputs; rates can be decimals (0.15) or percentages (15). */
function parseCsvRowToInputs(row: Record<string, string>): Partial<CalculatorInputs> {
  const num = (key: string) => {
    const v = row[key];
    if (v == null || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const out: Partial<CalculatorInputs> = {};
  if (num('followers') != null) out.followers = Math.max(0, Math.round(num('followers')!));
  if (num('reachRate') != null) out.reachRate = num('reachRate')! > 1 ? num('reachRate')! / 100 : num('reachRate')!;
  if (num('engagementRate') != null) out.engagementRate = num('engagementRate')! > 1 ? num('engagementRate')! / 100 : num('engagementRate')!;
  if (num('postsPerWeek') != null) out.postsPerWeek = Math.max(0, Math.round(num('postsPerWeek')!));
  if (num('conversionRate') != null) out.conversionRate = num('conversionRate')! > 1 ? num('conversionRate')! / 100 : num('conversionRate')!;
  if (num('avgOrderValue') != null) out.avgOrderValue = Math.max(0, num('avgOrderValue')!);
  if (num('monthlyAdSpend') != null) out.monthlyAdSpend = Math.max(0, num('monthlyAdSpend')!);
  return out;
}

const CSV_HEADER_ALIASES: Record<string, keyof CalculatorInputs> = {
  followers: 'followers',
  reachrate: 'reachRate',
  engagementrate: 'engagementRate',
  postsperweek: 'postsPerWeek',
  conversionrate: 'conversionRate',
  avgordervalue: 'avgOrderValue',
  monthlyadspend: 'monthlyAdSpend',
};

function parseCsvToInputs(csvText: string): CalculatorInputs | null {
  const lines = csvText.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return null;
  const header = lines[0].split(',').map((h) => h.trim().toLowerCase().replace(/\s+/g, ''));
  const values = lines[1].split(',').map((v) => v.trim());
  const row: Record<string, string> = {};
  header.forEach((h, i) => {
    const key = CSV_HEADER_ALIASES[h] ?? h;
    row[key] = values[i] ?? '';
  });
  const partial = parseCsvRowToInputs(row);
  if (Object.keys(partial).length === 0) return null;
  return { ...defaultInputs, ...partial };
}

const defaultInputs: CalculatorInputs = {
  followers: 10000,
  reachRate: 0.15, // % of followers reached per post
  engagementRate: 0.05, // engagement per reach
  postsPerWeek: 3,
  conversionRate: 0.02,
  avgOrderValue: 30,
  monthlyAdSpend: 0,
};

const CHART_WIDTH = 420;
const CHART_HEIGHT = 140;

function MiniLineChart({ series, series2, labels }: { series: number[]; series2?: number[]; labels?: string[] }) {
  const padding = 20;
  const all = series.concat(series2 || []);
  const max = Math.max(...all, 1);

  const toXY = (idx: number, val: number) => {
    const x = padding + (idx / Math.max(1, series.length - 1)) * (CHART_WIDTH - padding * 2);
    const y = CHART_HEIGHT - padding - (val / max) * (CHART_HEIGHT - padding * 2);
    return [x, y];
  };

  const path = series.map((v, i) => toXY(i, v).join(',')).join(' ');
  const path2 = series2 ? series2.map((v, i) => toXY(i, v).join(',')).join(' ') : undefined;

  return (
    <svg width={CHART_WIDTH} height={CHART_HEIGHT} role="img" aria-label="revenue chart">
      <rect x={0} y={0} width={CHART_WIDTH} height={CHART_HEIGHT} fill="none" />
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => (
        <line key={i} x1={padding} x2={CHART_WIDTH - padding} y1={padding + t * (CHART_HEIGHT - padding * 2)} y2={padding + t * (CHART_HEIGHT - padding * 2)} stroke="#eee" />
      ))}
      <polyline points={path} fill="none" stroke="#6C5CE7" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
      {path2 && <polyline points={path2} fill="none" stroke="#00B894" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />}
    </svg>
  );
}

export default function SocialMediaCalculatorPage() {
  const { data: session } = useSession();
  const userPlan = session?.user?.plan || 'FREE';
  const isProOrAbove = ['PRO', 'ENTERPRISE'].includes(userPlan);

  const [chartMounted, setChartMounted] = useState(false);
  useEffect(() => {
    setChartMounted(true);
  }, []);

  const [inputs, setInputs] = useState<CalculatorInputs>(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('sm_calculator_inputs') : null;
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<CalculatorInputs>;
        if (parsed && typeof parsed.followers === 'number') return { ...defaultInputs, ...parsed };
      }
    } catch (e) {
      /* ignore */
    }
    return defaultInputs;
  });
  const metrics = useMemo(() => calculateMetrics(inputs), [inputs]);
  const [scenarios, setScenarios] = useState<{ name: string; inputs: CalculatorInputs }[]>(() => {
    try {
      const raw = typeof window !== 'undefined' ? localStorage.getItem('sm_calculator_scenarios') : null;
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  });
  const [scenarioName, setScenarioName] = useState('');
  const maxScenarios = isProOrAbove ? 6 : 2;

  const calcAi = useAiActionCooldown(3000);
  const [aiSuggestions, setAiSuggestions] = useState<{ text: string; confidence: number }[]>([]);
  const [contentIdea, setContentIdea] = useState('');
  const [hashtagsInput, setHashtagsInput] = useState('');
  const aiDisabled = calcAi.disabled;
  const aiLoading = calcAi.inFlight;

  const [compareIndex, setCompareIndex] = useState<number | null>(null);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [baselineLoading, setBaselineLoading] = useState(false);
  const [baselineInfo, setBaselineInfo] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [projectedEngagementUplift, setProjectedEngagementUplift] = useState(0);
  const [postingOptimizer, setPostingOptimizer] = useState<{
    optimalPostingTimes: { dayOfWeek: number; day: string; time: string; platform: string }[];
    source: string;
    connectedSocial: string[];
    metricsDataSource?: string;
    metricsDataSourceLabel?: string;
  } | null>(null);

  const reportPdfRef = useRef<HTMLDivElement>(null);
  const csvInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('sm_calculator_scenarios', JSON.stringify(scenarios));
  }, [scenarios]);

  useEffect(() => {
    if (typeof window !== 'undefined') localStorage.setItem('sm_calculator_inputs', JSON.stringify(inputs));
  }, [inputs]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/analytics/audience-behavior');
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (cancelled) return;
        setPostingOptimizer({
          optimalPostingTimes: Array.isArray(data.optimalPostingTimes) ? data.optimalPostingTimes : [],
          source: typeof data.source === 'string' ? data.source : 'default',
          connectedSocial: Array.isArray(data.connectedSocial) ? data.connectedSocial : [],
          metricsDataSource: typeof data.metricsDataSource === 'string' ? data.metricsDataSource : undefined,
          metricsDataSourceLabel: typeof data.metricsDataSourceLabel === 'string' ? data.metricsDataSourceLabel : undefined,
        });
        setProjectedEngagementUplift(typeof data.projectedEngagementUplift === 'number' ? data.projectedEngagementUplift : 0);
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session?.user?.id]);

  const update = (patch: Partial<CalculatorInputs>) => setInputs((s) => ({ ...s, ...patch }));

  const saveScenario = () => {
    if (!scenarioName) return;
    if (scenarios.length >= maxScenarios) return;
    const list = [...scenarios];
    list.unshift({ name: scenarioName, inputs });
    if (list.length > maxScenarios) list.pop();
    setScenarios(list);
    setScenarioName('');
  };

  const applyScenario = (s: CalculatorInputs) => setInputs(s);

  const fetchAiSuggestions = () => {
    void calcAi.run(async () => {
      setAiSuggestions([]);
      const res = await fetch('/api/tools/social-calculator/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'suggest', inputs }),
      });
      const json = await res.json();
      if (json?.ok && Array.isArray(json.suggestions)) setAiSuggestions(json.suggestions);
    });
  };

  const fetchContentPrediction = () => {
    void calcAi.run(async () => {
      setAiSuggestions([]);
      const res = await fetch('/api/tools/social-calculator/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'predict', contentIdea }),
      });
      const json = await res.json();
      if (json?.ok && Array.isArray(json.suggestions)) setAiSuggestions(json.suggestions);
    });
  };

  const fetchHashtagImpact = () => {
    void calcAi.run(async () => {
      setAiSuggestions([]);
      const tags = hashtagsInput.split(',').map((t) => t.trim()).filter(Boolean);
      const res = await fetch('/api/tools/social-calculator/ai', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'hashtags', hashtags: tags }),
      });
      const json = await res.json();
      if (json?.ok && Array.isArray(json.suggestions)) setAiSuggestions(json.suggestions);
    });
  };

  const mainSeries = useMemo(() => projectRevenueTimeline(inputs, 12), [inputs]);
  const compareSeries = useMemo(() => (compareIndex !== null && scenarios[compareIndex] ? projectRevenueTimeline(scenarios[compareIndex].inputs, 12) : undefined), [compareIndex, scenarios]);
  const labels = useMemo(() => Array.from({ length: 12 }).map((_, i) => `M${i + 1}`), []);

  const logUsageMetric = async (action: string) => {
    try {
      await fetch('/api/tools/social-calculator/metrics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, user: session?.user?.email || 'anonymous', timestamp: new Date().toISOString() }),
      });
    } catch (e) {
      // non-fatal
    }
  };

  const openPrintableReport = () => {
    const html = `
      <html>
        <head><title>FloAI - Social Media Calculator Report</title></head>
        <body>
          <h1>Social Media Calculator Report</h1>
          <pre>${JSON.stringify({ inputs, metrics }, null, 2)}</pre>
        </body>
      </html>
    `;
    const w = window.open('', '_blank', 'noopener');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
      w.focus();
      logUsageMetric('export_printable');
    }
  };

  const fillBaselineFromConnectedAccounts = () => {
    if (!session?.user?.id) {
      setBaselineInfo({ type: 'info', text: 'Sign in to pull a baseline from connected social accounts.' });
      return;
    }
    setBaselineLoading(true);
    setBaselineInfo(null);
    void (async () => {
      try {
        const res = await fetch('/api/tools/social-calculator/baseline-from-accounts');
        const json = await res.json();
        if (!res.ok) {
          setBaselineInfo({ type: 'error', text: json?.error || 'Could not load connected accounts.' });
          setProjectedEngagementUplift(0);
          return;
        }
        if (!json.ok) {
          setBaselineInfo({ type: 'info', text: json.message || 'No social accounts to use.' });
          setProjectedEngagementUplift(0);
          return;
        }
        setInputs((s) => ({ ...s, ...defaultInputs, ...json.inputs }));
        setProjectedEngagementUplift(typeof json.projectedEngagementUplift === 'number' ? json.projectedEngagementUplift : 0);
        const prov =
          typeof json.metricsDataSourceLabel === 'string' ? `${json.metricsDataSourceLabel} ` : '';
        setBaselineInfo({
          type: 'success',
          text: `${prov}Applied baseline for: ${(json.connectedSocial as string[]).join(', ')}. ${json.note || ''}`,
        });
        void logUsageMetric('baseline_from_accounts');
      } catch {
        setBaselineInfo({ type: 'error', text: 'Network error loading baseline.' });
        setProjectedEngagementUplift(0);
      } finally {
        setBaselineLoading(false);
      }
    })();
  };

  const applyPostingTimeUplift = () => {
    if (!isProOrAbove || projectedEngagementUplift <= 0) return;
    update({ engagementRate: Math.min(0.5, inputs.engagementRate + projectedEngagementUplift) });
    void logUsageMetric('apply_posting_optimizer_uplift');
  };

  const handleCsvImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = reader.result as string;
      const parsed = parseCsvToInputs(text);
      if (parsed) {
        setInputs(parsed);
        logUsageMetric('import_csv');
      } else {
        setCsvError('Could not parse CSV. Use header: followers,reachRate,engagementRate,postsPerWeek,conversionRate,avgOrderValue,monthlyAdSpend');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadPdf = async () => {
    const el = reportPdfRef.current;
    if (!el || !isProOrAbove) return;
    try {
      const { generatePdfFromElement } = await import('@/lib/pdf/pdfFacade');
      const pdf = await generatePdfFromElement(el);
      pdf.save(`social-media-calculator-report-${new Date().toISOString().slice(0, 10)}.pdf`);
      logUsageMetric('export_pdf');
    } catch (err) {
      console.error('PDF export failed', err);
    }
  };

  return (
    <Box component="main" id="main-content" sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <IconButton component={Link} href="/tools" aria-label="Back to Tools">
          <ArrowBackIcon />
        </IconButton>
        <Icon icon={getToolIconForPath('/tools/social-media-calculator')} width={34} height={34} />
        <Typography variant="h4">Social Media Calculator</Typography>
      </Box>

      <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }} aria-labelledby="calculator-inputs">
              <Typography id="calculator-inputs" variant="h6" gutterBottom>Inputs</Typography>
              <Stack spacing={2}>
                <TextField
                  label="Total Followers"
                  type="number"
                  value={inputs.followers}
                  onChange={(e) => update({ followers: Number(e.target.value || 0) })}
                  inputProps={{ min: 0 }}
                />

                <Box>
                  <Typography gutterBottom>Estimated Reach Rate (% of followers reached per post)</Typography>
                  <Slider
                    value={Math.round(inputs.reachRate * 100)}
                    onChange={(_, v) => update({ reachRate: Number(v as number) / 100 })}
                    valueLabelDisplay="auto"
                    min={1}
                    max={100}
                  />
                </Box>

                <Box>
                  <Typography gutterBottom>Engagement Rate (engagements per reach)</Typography>
                  <Slider
                    value={Math.round(inputs.engagementRate * 100)}
                    onChange={(_, v) => update({ engagementRate: Number(v as number) / 100 })}
                    valueLabelDisplay="auto"
                    min={0}
                    max={50}
                  />
                </Box>

                <TextField
                  label="Posts per week"
                  type="number"
                  value={inputs.postsPerWeek}
                  onChange={(e) => update({ postsPerWeek: Number(e.target.value || 0) })}
                  inputProps={{ min: 0 }}
                />

                <Box>
                  <Typography gutterBottom>Conversion Rate (of engagements)</Typography>
                  <Slider
                    value={Math.round(inputs.conversionRate * 100)}
                    onChange={(_, v) => update({ conversionRate: Number(v as number) / 100 })}
                    valueLabelDisplay="auto"
                    min={0}
                    max={50}
                  />
                </Box>

                <TextField
                  label="Avg Order Value (USD)"
                  type="number"
                  value={inputs.avgOrderValue}
                  onChange={(e) => update({ avgOrderValue: Number(e.target.value || 0) })}
                />

                <TextField
                  label="Monthly Ad Spend (USD)"
                  type="number"
                  value={inputs.monthlyAdSpend}
                  onChange={(e) => update({ monthlyAdSpend: Number(e.target.value || 0) })}
                />

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Scenario</Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField size="small" placeholder="Scenario name" value={scenarioName} onChange={(e) => setScenarioName(e.target.value)} />
                    <Button variant="contained" onClick={saveScenario} disabled={scenarios.length >= maxScenarios}>
                      Save Scenario
                    </Button>
                  </Stack>
                  {scenarios.length >= maxScenarios && !isProOrAbove && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Upgrade to Pro to save more scenarios (Free: {maxScenarios} max).
                    </Typography>
                  )}
                  {scenarios.length > 0 && (
                    <Stack spacing={1} sx={{ mt: 1 }}>
                      {scenarios.map((s, i) => (
                        <Stack key={i} direction="row" spacing={1} alignItems="center">
                          <Button variant="outlined" onClick={() => applyScenario(s.inputs)}>{s.name}</Button>
                          <Button onClick={() => setScenarios((prev) => prev.filter((_, idx) => idx !== i))}>Remove</Button>
                        </Stack>
                      ))}
                    </Stack>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Import baseline (CSV)</Typography>
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    aria-label="Import CSV file"
                    style={{ display: 'none' }}
                    onChange={handleCsvImport}
                  />
                  <Button variant="outlined" onClick={() => csvInputRef.current?.click()}>
                    Import CSV
                  </Button>
                  {csvError && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>{csvError}</Typography>
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>Connected accounts (OAuth)</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Fill followers, reach, engagement, and post frequency from platforms linked in your profile (heuristic — replace with real metrics when available).
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      onClick={fillBaselineFromConnectedAccounts}
                      disabled={baselineLoading}
                      aria-label="Apply calculator baseline from connected social accounts"
                    >
                      {baselineLoading ? <CircularProgress size={18} /> : 'Fill from connected accounts'}
                    </Button>
                    <Button component={Link} href="/dashboard/profile" variant="text" size="small">
                      Manage connections
                    </Button>
                  </Stack>
                  {baselineInfo && (
                    <Alert severity={baselineInfo.type === 'success' ? 'success' : baselineInfo.type === 'error' ? 'error' : 'info'} sx={{ mt: 1 }}>
                      {baselineInfo.text}
                    </Alert>
                  )}
                </Box>

              </Stack>
            </Paper>

            <Paper sx={{ p: 2, mt: 2 }}>
              <Typography variant="h6" gutterBottom>AI Tools</Typography>

              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2">Get Suggestions</Typography>
                <Button variant="contained" sx={{ mt: 1 }} onClick={fetchAiSuggestions} disabled={aiDisabled}>
                  {aiLoading ? <CircularProgress size={18} /> : 'Get AI Suggestions'}
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Predict Content Performance</Typography>
                <TextField fullWidth size="small" placeholder="Describe your content idea..." value={contentIdea} onChange={(e) => setContentIdea(e.target.value)} sx={{ mt: 1 }} />
                <Button variant="outlined" sx={{ mt: 1 }} onClick={fetchContentPrediction} disabled={aiDisabled || !contentIdea}>{aiLoading ? <CircularProgress size={18} /> : 'Predict Performance'}</Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Hashtag Impact</Typography>
                <TextField fullWidth size="small" placeholder="#hashtag1, #hashtag2" value={hashtagsInput} onChange={(e) => setHashtagsInput(e.target.value)} sx={{ mt: 1 }} />
                <Button variant="outlined" sx={{ mt: 1 }} onClick={fetchHashtagImpact} disabled={aiDisabled || !hashtagsInput}>{aiLoading ? <CircularProgress size={18} /> : "Estimate Hashtag Impact"}</Button>
              </Box>

            </Paper>

          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }} aria-labelledby="calculator-results">
              <Typography id="calculator-results" variant="h6" gutterBottom>Results</Typography>

              <Stack spacing={1}>
                <Typography>Estimated Weekly Reach: <strong>{metrics.weeklyReach.toLocaleString()}</strong></Typography>
                <Typography>Estimated Weekly Impressions: <strong>{metrics.weeklyImpressions.toLocaleString()}</strong></Typography>
                <Typography>Estimated Weekly Engagements: <strong>{metrics.weeklyEngagements.toLocaleString()}</strong></Typography>
                <Typography>Estimated Monthly Conversions: <strong>{metrics.monthlyConversions.toLocaleString()}</strong></Typography>
                <Typography>Estimated Monthly Revenue: <strong>${metrics.monthlyRevenue.toFixed(2)}</strong></Typography>
                <Typography>Estimated Monthly ROI: <strong>{metrics.roiPercent.toFixed(1)}%</strong></Typography>
              </Stack>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Quick Actions</Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                  <Button onClick={() => { navigator.clipboard?.writeText(JSON.stringify({ inputs, metrics })); }}>Copy Results JSON</Button>
                  <Button onClick={() => { setInputs(defaultInputs); }}>Reset</Button>
                  {isProOrAbove ? (
                    <>
                      <Button onClick={openPrintableReport}>Open Printable Report</Button>
                      <Button onClick={downloadPdf}>Download PDF</Button>
                    </>
                  ) : (
                    <Button href="/upgrade">Upgrade to export</Button>
                  )}
                </Stack>
              </Box>

              {/* Hidden report div for PDF export (off-screen but rendered for html2canvas) */}
              <Box
                ref={reportPdfRef}
                sx={{
                  position: 'absolute',
                  left: -9999,
                  top: 0,
                  width: 600,
                  p: 2,
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  fontFamily: 'sans-serif',
                }}
                aria-hidden="true"
              >
                <Typography variant="h5">Social Media Calculator Report</Typography>
                <Typography variant="body2" color="text.secondary">{new Date().toLocaleDateString()}</Typography>
                <Box component="section" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Inputs</Typography>
                  <Typography variant="body2">Followers: {inputs.followers.toLocaleString()} · Reach rate: {(inputs.reachRate * 100).toFixed(1)}% · Engagement rate: {(inputs.engagementRate * 100).toFixed(1)}% · Posts/week: {inputs.postsPerWeek} · Conversion rate: {(inputs.conversionRate * 100).toFixed(1)}% · Avg order: ${inputs.avgOrderValue} · Monthly ad spend: ${inputs.monthlyAdSpend}</Typography>
                </Box>
                <Box component="section" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Results</Typography>
                  <Typography variant="body2">Weekly reach: {metrics.weeklyReach.toLocaleString()} · Weekly impressions: {metrics.weeklyImpressions.toLocaleString()} · Weekly engagements: {metrics.weeklyEngagements.toLocaleString()} · Monthly conversions: {metrics.monthlyConversions.toLocaleString()} · Monthly revenue: ${metrics.monthlyRevenue.toFixed(2)} · ROI: {metrics.roiPercent.toFixed(1)}%</Typography>
                </Box>
              </Box>

              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1">AI Suggestions</Typography>
                {aiLoading && <Typography sx={{ mt: 1 }}>Thinking... <CircularProgress size={14} /></Typography>}
                {!aiLoading && aiSuggestions.length === 0 && (
                  <Typography color="text.secondary" sx={{ mt: 1 }}>No suggestions yet. Click "Get AI Suggestions" or try the other AI tools.</Typography>
                )}
                {!aiLoading && aiSuggestions.length > 0 && (
                  <Stack spacing={1} sx={{ mt: 1 }}>
                    {aiSuggestions.map((s, i) => (
                      <Paper key={i} sx={{ p: 1 }}>
                        <Typography>{s.text}</Typography>
                        <Typography variant="caption" color="text.secondary">Confidence: {(s.confidence * 100).toFixed(0)}%</Typography>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>

              <Paper sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle1">Projection (12 months)</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  {chartMounted ? (
                    <MiniLineChart series={mainSeries} series2={compareSeries} labels={labels} />
                  ) : (
                    <svg width={CHART_WIDTH} height={CHART_HEIGHT} role="img" aria-label="revenue chart placeholder"><rect x={0} y={0} width={CHART_WIDTH} height={CHART_HEIGHT} fill="none" /></svg>
                  )}
                </Box>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center" sx={{ mt: 1 }}>
                  <FormControl size="small" sx={{ width: { xs: '100%', sm: 180 } }}>
                    <InputLabel id="compare-label">Compare</InputLabel>
                    <Select labelId="compare-label" value={compareIndex ?? ''} label="Compare" onChange={(e) => setCompareIndex(e.target.value === '' ? null : Number(e.target.value))}>
                      <MenuItem value="">None</MenuItem>
                      {scenarios.map((s, i) => (
                        <MenuItem key={i} value={i}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Button sx={{ width: { xs: '100%', sm: 'auto' } }} onClick={() => {
                    const csv = ['month,revenue', ...mainSeries.map((v, i) => `${i+1},${v.toFixed(2)}`)].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'projection.csv'; a.click();
                    URL.revokeObjectURL(url);
                    logUsageMetric('export_csv');
                  }}>{isProOrAbove ? 'Export CSV' : 'Upgrade to export'}</Button>

                </Stack>
              </Paper>

              <Paper sx={{ p: 2, mt: 2 }} component="section" aria-labelledby="posting-times-heading">
                <Typography id="posting-times-heading" variant="subtitle1" gutterBottom>Suggested posting times</Typography>
                {postingOptimizer?.metricsDataSourceLabel && (
                  <Alert severity="info" sx={{ mb: 1.5 }} role="note">
                    {postingOptimizer.metricsDataSourceLabel}
                  </Alert>
                )}
                {postingOptimizer && postingOptimizer.optimalPostingTimes.length > 0 && postingOptimizer.source !== 'default' ? (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Based on your connected channels ({postingOptimizer.connectedSocial.join(', ')}). Same windows power the calendar “optimal time” highlights.
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {postingOptimizer.optimalPostingTimes.map((s, i) => (
                        <Typography key={`${s.day}-${s.time}-${i}`} component="li" variant="body2">
                          <strong>{s.day}</strong> {s.time} · {s.platform}
                        </Typography>
                      ))}
                    </Box>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    For higher engagement, aim for <strong>Tue–Thu 9–11am or 7–9pm</strong> in your audience’s time zone. Connect social accounts in Profile for platform-specific windows.
                  </Typography>
                )}
                {isProOrAbove && projectedEngagementUplift > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Posting Time Optimizer (model): add up to <strong>{(projectedEngagementUplift * 100).toFixed(1)}%</strong> to engagement rate to reflect scheduling in these windows (editable).
                    </Typography>
                    <Button size="small" variant="contained" onClick={applyPostingTimeUplift}>
                      Apply uplift to engagement rate
                    </Button>
                  </Box>
                )}
                {!isProOrAbove && postingOptimizer && postingOptimizer.source === 'default' && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Upgrade to Pro for one-click uplift from suggested posting windows when you use “Fill from connected accounts”.
                  </Typography>
                )}
              </Paper>

            </Paper>
          </Grid>
        </Grid>
    </Box>
  );
}
